import { isNull, uniqBy } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { DoraFunctionCompany, DoraSupplyChainDetails, NewSupplyChainElement, SupplyChain, SupplyChainElement, DoraFunctionCompanyDetails, SupplyChainElementTab, DoraSupplyChainContractualInfo, DoraSupplyChainAssessmentInfo, DoraSupplyChainAssessmentTimeline, DoraDocumentPreview, IctContractDisplay } from './types';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { newSupplyChainElement } from './reducer';
import { TableFilters } from '../../../shared/table/types';
import { getDoraSupplyChainRoot } from '../../store/selectors';

export const getDoraFunctionCompanies = (state: ApplicationState): DoraFunctionCompany[] => getDoraSupplyChainRoot(state).doraFunctionCompanyDetails;

export const getSelectedCompanyId = (state: ApplicationState): number | null => getDoraSupplyChainRoot(state).selectedCompanyId;

export const getSelectedFunctionId = (state: ApplicationState): number | null => getDoraSupplyChainRoot(state).selectedFunctionId;

export const getDoraSupplyChainCompanies = (state: ApplicationState): { value: number; label: string; }[] => {
    let functionCompanyPairs = getDoraFunctionCompanies(state);
    const selectedFunction = getSelectedFunctionId(state);
    if (!isNull(selectedFunction)) {
        functionCompanyPairs = functionCompanyPairs.filter(({ doraFunctionId }) => doraFunctionId === selectedFunction);
    }
    return uniqBy('value', functionCompanyPairs.map(({ companyName, companyId }) => ({ value: companyId, label: companyName })));
};

export const getDoraSupplyChainFunctions = (state: ApplicationState): { value: number; label: string; }[] => {
    let functionCompanyPairs = getDoraFunctionCompanies(state);
    const selectedCompanyId = getSelectedCompanyId(state);
    if (!isNull(selectedCompanyId)) {
        functionCompanyPairs = functionCompanyPairs.filter(({ companyId }) => companyId === selectedCompanyId);
    }
    return uniqBy('value', functionCompanyPairs.map(({ functionName, doraFunctionId }) => ({ value: doraFunctionId, label: functionName })));
};

export const getIsFetchingDoraFunctionCompanyDetails = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).isFetchingDoraFunctionCompanyDetails;

export const getResetFunctionCompanyButtonVisible = (state: ApplicationState): boolean => !isNull(getSelectedCompanyId(state)) || !isNull(getSelectedFunctionId(state));

export const getCompanyAndFunctionSelected = (state: ApplicationState): boolean => !isNull(getSelectedCompanyId(state)) && !isNull(getSelectedFunctionId(state));

export const getDroppableHeight = (state: ApplicationState): number => getDoraSupplyChainRoot(state).droppableHeight;

export const getCurrentSupplyChain = (state: ApplicationState): SupplyChain | null => getDoraSupplyChainRoot(state).currentSupplyChain;

export const getDetailsInformation = (state: ApplicationState): DoraSupplyChainDetails => {
    const selectedId = getSelectedSupplyChainId(state);
    if (selectedId) {
        const supplyChainElements = getSupplyChainElements(state);
        const selectedChainElement: SupplyChainElement | undefined = supplyChainElements.find(({ doraSupplyChainId }) => doraSupplyChainId === selectedId);
        if (selectedChainElement) {
            return selectedChainElement.details;
        }
    }
    const newSupplyChainElement = getNewSupplyChainElement(state);
    return newSupplyChainElement.details;
};

export const getSavedDetailsInformation = (state: ApplicationState): DoraSupplyChainDetails => {
    const selectedId = getSelectedSupplyChainId(state);
    if (selectedId) {
        const supplyChainElements = getSavedSupplyChainElements(state);
        const selectedChainElement: SupplyChainElement | undefined = supplyChainElements.find(({ doraSupplyChainId }) => doraSupplyChainId === selectedId);
        if (selectedChainElement) {
            return selectedChainElement.details;
        }
    }
    return newSupplyChainElement.details;
};

export const getSavedSupplyChain = (state: ApplicationState): SupplyChain | null => getDoraSupplyChainRoot(state).savedSupplyChain;

export const getSupplyChainElements = (state: ApplicationState): SupplyChainElement[] => getCurrentSupplyChain(state)?.supplyChain || [];

export const getSavedSupplyChainElements = (state: ApplicationState): SupplyChainElement[] => getSavedSupplyChain(state)?.supplyChain || [];

export const getSupplyChainDetailsModalOpen = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).supplyChainDetailsModalOpen;

export const getIsSaving = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).isSaving;

export const getSupplyChainElementUpdated = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).supplyChainElementHasUpdated;

export const getSupplyChainUpdated = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).supplyChainHasUpdated;

export const getSelectedSupplyChainId = (state: ApplicationState): number | null => getDoraSupplyChainRoot(state).selectedSupplyChainId;

export const getNewSupplyChainElement = (state: ApplicationState): NewSupplyChainElement => getDoraSupplyChainRoot(state).newSupplyChainElement;

export const getAvailableCompanies = (state: ApplicationState): DropdownOption[] => getDoraSupplyChainRoot(state).availableCompanies;

export const getAvailableThirdPartyCompanies = (state: ApplicationState): DropdownOption[] => getDoraSupplyChainRoot(state).availableThirdPartyCompanies;

export const getCompanyFunctionFilters = (state: ApplicationState): TableFilters => getDoraSupplyChainRoot(state).supplyChainFilters;

export const getFunctionsActive = (state: ApplicationState): boolean => Object.values(getCompanyFunctionFilters(state)).some(({ text, dropdown }) => text.length > 0 || !isNull(dropdown));

export const getDeleteConfirmationModalOpen = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).deleteElementConfirmationModalOpen;

export const getIsDeleting = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).isDeleting;

export const getSavedFunctionCompanyDetails = (state: ApplicationState): DoraFunctionCompanyDetails | null => getSavedSupplyChain(state)?.details || null;

export const getCurrentFunctionCompanyDetails = (state: ApplicationState): DoraFunctionCompanyDetails | null => getCurrentSupplyChain(state)?.details || null;

export const getFunctionCompanyName = (state: ApplicationState): string => {
    const savedSupplyChain = getSavedSupplyChain(state);
    if (savedSupplyChain) {
        return `${savedSupplyChain.companyName} - ${savedSupplyChain.functionName}`;
    }
    return '';
};

export const getServiceDescriptionModalOpen = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).serviceDescriptionOpen;

export const getSelectedElementWizardTab = (state: ApplicationState): SupplyChainElementTab => getDoraSupplyChainRoot(state).selectedElementWizardTab;

export const getSelectedElementContractualInfo = (state: ApplicationState): DoraSupplyChainContractualInfo | null => getDoraSupplyChainRoot(state).supplyChainElementDetails.contractualInfo;

export const getSelectedElementAssessmentInfo = (state: ApplicationState): DoraSupplyChainAssessmentInfo => getDoraSupplyChainRoot(state).supplyChainElementDetails.assessmentInfo;
export const getSavedElementAssessmentInfo = (state: ApplicationState): DoraSupplyChainAssessmentInfo => getDoraSupplyChainRoot(state).savedSupplyChainElementDetails.assessmentInfo;

export const getSupplyChainElementAssessmentTimeline = (state: ApplicationState): DoraSupplyChainAssessmentTimeline[] => getDoraSupplyChainRoot(state).supplyChainElementDetails.assessmentTimeline;
export const getSelectedAssessmentTimeline = (state: ApplicationState): DoraSupplyChainAssessmentTimeline | null => getDoraSupplyChainRoot(state).selectedSupplyChainAssessmentTimeline;

export const getUnsavedChangedModalOpen = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).unsavedChangesModalOpen;

export const getDoraDocumentPreview = (state: ApplicationState): DoraDocumentPreview | null => getDoraSupplyChainRoot(state).preview;

export const getIsOpening = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).isOpening;
export const getIsUploading = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).isUploading;
export const getIsDownloading = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).isDownloading;

export const getPreviewModalOpen = (state: ApplicationState): boolean => getIsOpening(state) || !isNull(getDoraDocumentPreview(state));

export const getAssessmentInfoFiles = (state: ApplicationState): File[] => getDoraSupplyChainRoot(state).assessmentInfoFiles;

export const getHasDoraFilesToUpload = (state: ApplicationState): boolean => getAssessmentInfoFiles(state).length > 0;

export const getIsLinkingContract = (state: ApplicationState): boolean => getDoraSupplyChainRoot(state).linkingContract;

export const getIctContractDisplay = (state: ApplicationState): IctContractDisplay => getDoraSupplyChainRoot(state).ictContractDisplay;
